import React, { useEffect, useMemo, useRef } from 'react';
import { useSiteConfig } from '@innovyze/stylovyze';
import {
  ADP_EVENTS,
  initBernoulliAdp,
  trackAdpAppView,
  trackAdpEvent,
} from '@/services/analytics/adp';
import { BERNOULLI_ADP_KEY } from '@/config/adp.site-config';

import { ChatInterface } from '@/components/chat/';

type WebContainerLike = {
  getSessionId?: () => string;
};

const getHostSessionId = () => {
  const hostBridge = (window as Window & { WebContainer?: WebContainerLike })
    .WebContainer;
  return typeof hostBridge?.getSessionId === 'function'
    ? hostBridge.getSessionId()
    : undefined;
};

const BernoulliHome = () => {
  const { config } = useSiteConfig();
  const foregroundStartRef = useRef<number>(0);
  const activeDurationMsRef = useRef<number>(0);

  const adp = useMemo(() => {
    const adpConfig = config?.adp;
    const hostReferrer =
      adpConfig?.id && adpConfig.name && adpConfig.key !== BERNOULLI_ADP_KEY
        ? {
            id: adpConfig.id,
            name: adpConfig.name,
            sessionId: getHostSessionId(),
          }
        : undefined;
    return initBernoulliAdp({
      runtimeConfig: adpConfig,
      hostReferrer,
    });
  }, [config]);

  useEffect(() => {
    // Enables host product lineage when running as embedded add-in.
    adp?.enableHostProductData();

    trackAdpAppView(adp, {
      route: window.location.pathname,
    });
  }, [adp]);

  useEffect(() => {
    const now = Date.now();
    foregroundStartRef.current =
      document.visibilityState === 'visible' ? now : 0;

    const flushForegroundDuration = () => {
      if (foregroundStartRef.current > 0) {
        activeDurationMsRef.current += Date.now() - foregroundStartRef.current;
        foregroundStartRef.current = 0;
      }

      trackAdpEvent(adp, ADP_EVENTS.APP_FOREGROUND_DURATION, {
        duration_ms: String(activeDurationMsRef.current),
        route: window.location.pathname,
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        foregroundStartRef.current = Date.now();
        return;
      }

      if (foregroundStartRef.current > 0) {
        activeDurationMsRef.current += Date.now() - foregroundStartRef.current;
        foregroundStartRef.current = 0;
      }
    };

    const onPageHide = () => {
      flushForegroundDuration();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      flushForegroundDuration();
    };
  }, [adp]);

  return <ChatInterface />;
};

export default BernoulliHome;
